import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { Logger } from '../logger';
import { NotificationService, ModalDialogNotificationService } from '../notification.service';

import { CustomException, ExceptionFactory, Importance } from './exception.factory';

// Url che possono essere ignorate quando generano errori. Es: Viewer chiuso
const IGNORABLE_URL_ERRORS = [/\/suitestensa\/web\/api/];

@Injectable({
    providedIn: 'root',
})
export class ErrorsHandler implements ErrorHandler {
    // constructor

    constructor(
        // @Optional() private notificationService: EstensaNotificationService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private ng2NotificationService: NotificationService,
        private router: Router,
        private translate: TranslateService,
    ) {}

    // public functions

    handleError(response: Error | HttpErrorResponse) {
        if (response instanceof HttpErrorResponse) {
            for (const url of IGNORABLE_URL_ERRORS) {
                if (url.test(response.url)) {
                    return;
                }
            }
        }

        let errorMessage = this.tryGetEstensaFaultErrorMessage(response);
        let uid = response instanceof HttpErrorResponse ? response.headers.get('X-Correlation-ID') : undefined;
        if (this.shouldLog(response)) {
            this.logger.error(errorMessage, uid);
        }

        if (response instanceof HttpErrorResponse) {
            this.handleHttpError(response);
        } else if (!this.router.url.startsWith('/new/vnext')) {
            this.notifyError(`${errorMessage}`, this.translate.instant('ApplicationError'));
        }
    }

    // private functions

    private tryGetEstensaFaultErrorMessage(response: Error | HttpErrorResponse): string {
        let errorMessage = response.message;

        let estensaMessage =
            response instanceof HttpErrorResponse ? response?.error?.estensaFaultDetails?.estensaFault?.message : null;
        if (estensaMessage) {
            errorMessage += '\nESTENSA FAULT MESSAGE: ' + estensaMessage;
        }

        return errorMessage;
    }

    private buildCustomException(error) {
        let estensaFault = error?.estensaFaultDetails?.estensaFault;
        if (!estensaFault) {
            return null;
        }

        try {
            return ExceptionFactory.create(
                error.estensaFaultDetails.typeName,
                error.estensaFaultDetails.estensaFault,
                this.translate,
            );
        } catch (exception) {
            let message = 'Error during CustomException creation';
            console.log(message);
            console.log('typeName: ' + error?.estensaFaultDetails?.typeName);
            console.log('faultDetails:');
            console.log(error?.estensaFaultDetails?.estensaFault);
            console.log('exception:');
            console.log(exception);

            message += ': ' + exception.message;

            this.notifyError(message, this.translate.instant('ApplicationError'));
        }

        return null;
    }

    private handleHttpError(response: HttpErrorResponse) {
        let errorMessage = this.tryGetEstensaFaultErrorMessage(response);

        /* MB 15-11-2021: Classe CustomException viene usata? Roba di Antonio, verificare ed eventualmente buttare via... */
        let exception = this.buildCustomException(response.error);
        if (exception != null && exception instanceof CustomException) {
            return this.notifyCustomException(exception);
        }

        if (response.status == 401) {
            return this.modalDialogNotificationService
                .error(
                    this.translate.instant('UnauthorizedOperationOrExpiredSession') +
                        '. ' +
                        this.translate.instant('RedirectToLogin'),
                )
                .subscribe(() => {
                    window.location.reload();
                });
        }

        if (response.status == 403) {
            return this.modalDialogNotificationService.warning(
                this.translate.instant('UnauthorizedOperationOrExpiredSession'),
            );
        }

        if (!navigator.onLine) {
            return this.notifyError(
                this.translate.instant('NoInternetConnection'),
                this.translate.instant('ServerWarning'),
            );
        }

        return this.notifyError(
            `HTTP STATUS: ${response.status} \nHTTP ERROR: ${errorMessage}`,
            this.translate.instant('Exception_ProxyFault'),
        );
    }

    private notifyCustomException(exception: CustomException) {
        switch (exception.importance) {
            case Importance.high:
                return this.modalDialogNotificationService.error(
                    exception.message,
                    exception.title != null ? exception.title : this.translate.instant('ServerError'),
                );
            case Importance.medium:
                return this.notifyError(
                    exception.message,
                    exception.title != null ? exception.title : this.translate.instant('ServerWarning'),
                );
            case Importance.low:
                return this.notifyError(
                    exception.message,
                    exception.title != null ? exception.title : this.translate.instant('Information'),
                );
            default:
                return null;
        }
    }

    private notifyError(message: string, title: string) {
        this.ng2NotificationService.error(message, title);
    }

    private shouldLog(response: null | Error | HttpErrorResponse) {
        // Logga tutti gli errori non-HTTP.
        if (!(response instanceof HttpErrorResponse)) {
            return true;
        }

        // Non loggare in caso di connettività assente.
        if (response.status === 0) {
            return false;
        }

        // Non loggare errori del logger.
        if (response.url && response.url.match(/SeLog/i)) {
            return false;
        }

        return true;
    }
}
